import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Upload, Loader2, CheckCircle, AlertTriangle, Copy, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { canWriteCertFields } from '../lib/roles';
import {
  CERT_IMPORT_COLUMNS,
  CERT_IMPORT_PROBLEM_LABELS,
  CERT_IMPORT_STATUS_LABELS,
  type CertDaysImportRequest,
  type CertDayImportMapping,
  type CertDayImportPreview,
  type CertDayImportResolution,
  type CertDayImportRow,
  type CertDayImportRowStatus,
  type CertDayImportCommitResult,
  type CertImportRosterUnit,
  type CertImportSheet,
  type CertImportUploadResult,
} from '../types/cert-day-import';

/**
 * Historical certification days, off a paper log, in bulk.
 *
 * ── WHAT THIS SCREEN IS FOR ─────────────────────────────────────────────────
 *
 * TNT keeps the runs a tool did before FieldIQ on paper. Entering them one at a
 * time through the certification card is the door that exists; this is the same
 * door for a year of them. Every row it commits is an ordinary `historical_day`
 * ledger entry — the import is not a second way of counting cert days, it is a
 * faster way of typing the same ones.
 *
 * ── THE OPERATOR MAPS THE COLUMNS. NOTHING IS GUESSED ───────────────────────
 *
 * There is no auto-detect and there should not be one. These sheets are paper
 * transcriptions with no consistent format, and a header-name guess that is
 * right nine times in ten puts a wrong cert day on a document an oil company
 * reads the tenth time.
 *
 * ── THREE STEPS, AND THE MIDDLE ONE WRITES NOTHING ──────────────────────────
 *
 *   upload    the file is read server-side into a header and rows
 *   preview   what WOULD happen — re-run on every mapping or resolution change
 *   commit    one transaction, one batch id, all rows or none
 *
 * Preview is safe to call repeatedly; it is a read.
 *
 * ── THE COMMIT BUTTON'S GATE IS THE SERVER'S ANSWER, NOT THIS FILE'S ────────
 *
 * `preview.committable` comes from the API. This screen never computes it from
 * the row statuses it happens to be rendering — two answers to one question,
 * and the one that matters is the one the commit endpoint will use. If they
 * ever disagree, the button is wrong and the server still refuses.
 */

type Phase = 'idle' | 'mapping' | 'done';

const EMPTY_MAPPING: CertDayImportMapping = {
  serial: null, days: null, workDate: null, rigName: null, customerName: null, note: null,
};

const STATUS_STYLES: Record<CertDayImportRowStatus, string> = {
  matched: 'bg-emerald-500/15 text-emerald-400',
  resolved: 'bg-sky-500/15 text-sky-400',
  ambiguous: 'bg-amber-500/15 text-amber-400',
  unmatched: 'bg-amber-500/15 text-amber-400',
  invalid: 'bg-red-500/15 text-red-400',
};

/** An absent value renders as a dash. Never 0, never blank. */
function dash(value: string | number | null | undefined): React.ReactNode {
  if (value === null || value === undefined || value === '') return <span className="text-fieldiq-muted">—</span>;
  return value;
}

function unitLabel(u: CertImportRosterUnit): string {
  // name · type · serial. Names are NOT unique within a tenant — TNT has 157
  // distinct names across 276 units — so the name alone cannot identify a unit
  // in a picker.
  return [u.name, u.type, u.serialNumber].filter(Boolean).join(' · ');
}

export default function CertDayImportScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [upload, setUpload] = useState<CertImportUploadResult | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [mapping, setMapping] = useState<CertDayImportMapping>(EMPTY_MAPPING);
  const [resolutions, setResolutions] = useState<Record<string, CertDayImportResolution>>({});
  const [previewData, setPreviewData] = useState<CertDayImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CertDayImportCommitResult | null>(null);

  // Hidden here AND refused server-side. The gate that matters is the API's —
  // this only keeps a door out of sight that a person could not walk through.
  const canWrite = canWriteCertFields(user?.role);

  const sheet: CertImportSheet | null = upload?.sheets[sheetIndex] ?? null;

  const requestBody = useMemo(() => {
    if (!sheet) return null;
    return { rows: sheet.rows, columns: sheet.columns, mapping, resolutions };
  }, [sheet, mapping, resolutions]);

  const mappingComplete =
    mapping.serial !== null && mapping.days !== null && mapping.workDate !== null;

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.equipment.certDaysImportFile(file),
    onSuccess: (res: any) => {
      const body = res.data;
      // The envelope first, then the payload. `truncated`/`parsedCount` ride as
      // siblings of `data` AND on it; a field read only off the payload has
      // historically never rendered.
      const payload = body.data ?? body;
      const data: CertImportUploadResult = {
        fileName: payload.fileName,
        sheets: payload.sheets ?? [],
        rowCount: payload.rowCount ?? 0,
        parsedCount: body.parsedCount ?? payload.parsedCount ?? payload.rowCount ?? 0,
        truncated: (body.truncated ?? payload.truncated) === true,
      };
      setUpload(data);
      setSheetIndex(0);
      setMapping(EMPTY_MAPPING);
      setResolutions({});
      setPreviewData(null);
      setError(null);
      setPhase('mapping');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || err.message || 'Failed to read that file');
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const previewMutation = useMutation({
    // Takes its body as an ARGUMENT, not from the closure. `setState` is async,
    // so a mutationFn reading `requestBody` would preview the mapping the
    // operator had a moment ago — an answer to a question they already changed.
    mutationFn: (body: CertDaysImportRequest) => api.equipment.certDaysImportPreview(body),
    onSuccess: (res: any) => {
      setPreviewData((res.data?.data ?? res.data) as CertDayImportPreview);
      setError(null);
    },
    onError: (err: any) => {
      setPreviewData(null);
      setError(err.response?.data?.error || err.message || 'Failed to build the preview');
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => api.equipment.certDaysImportConfirm(requestBody as CertDaysImportRequest),
    onSuccess: (res: any) => {
      setResult((res.data?.data ?? res.data) as CertDayImportCommitResult);
      setPhase('done');
      // A counter moved on every unit this touched, so anything that renders one
      // is stale. Without these the screens behind this one keep the old number
      // until a hard refresh and read as "the import did nothing".
      for (const key of [
        ['equipment'], ['equipment-cert-summary'], ['equipment-report'],
        ['home-equipment'], ['field-my-equipment'],
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      toast.success(res.data?.message || 'Certification days recorded');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || err.message || 'Failed to record the import');
    },
  });

  /** Re-preview whenever the mapping or a resolution changes. */
  function refreshPreview(next?: {
    mapping?: CertDayImportMapping;
    resolutions?: Record<string, CertDayImportResolution>;
  }) {
    const m = next?.mapping ?? mapping;
    if (m.serial === null || m.days === null || m.workDate === null) {
      setPreviewData(null);
      return;
    }
    // Built from the arguments rather than from state: setState is async, and
    // previewing the PREVIOUS mapping would show the operator an answer to a
    // question they had already changed.
    const body = {
      rows: sheet?.rows ?? [],
      columns: sheet?.columns ?? [],
      mapping: m,
      resolutions: next?.resolutions ?? resolutions,
    };
    previewMutation.mutate(body as CertDaysImportRequest);
  }

  function setColumn(key: keyof CertDayImportMapping, value: string) {
    const next = { ...mapping, [key]: value === '' ? null : Number(value) };
    setMapping(next);
    refreshPreview({ mapping: next });
  }

  function resolveRow(rowNumber: number, patch: CertDayImportResolution) {
    const next = {
      ...resolutions,
      [String(rowNumber)]: { ...resolutions[String(rowNumber)], ...patch },
    };
    setResolutions(next);
    refreshPreview({ resolutions: next });
  }

  function reset() {
    setPhase('idle');
    setUpload(null);
    setMapping(EMPTY_MAPPING);
    setResolutions({});
    setPreviewData(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  if (!canWrite) {
    return (
      <div className="min-h-full bg-background">
        <Header title="Import Certification Days" onBack={() => navigate(-1)} />
        <div className="px-6 py-4">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-foreground font-semibold">
                Your role cannot record certification days.
              </p>
              <p className="text-sm text-fieldiq-text mt-1">
                Ask an admin, office staff member or dispatcher to run the import.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <Header
        title="Import Certification Days"
        onBack={() => navigate(-1)}
        actions={
          phase !== 'idle' && (
            <button
              onClick={reset}
              className="bg-fieldiq-card border border-fieldiq-border text-foreground font-semibold px-4 py-2 rounded-lg text-sm hover:bg-secondary/50 transition-colors"
            >
              Start over
            </button>
          )
        }
      />

      {/* The bottom padding is not decoration. The floating agent button is
          fixed to the bottom-right at 72px square, and on a short file the
          Commit button lands exactly underneath it — found by opening this
          screen in a browser, not by reading the markup.
          A plain `pb-32` rather than an arbitrary calc of `--bottom-chrome`:
          the calc version was tried first and rendered as 16px, because the
          rule was never emitted. The class was in the DOM and the padding was
          not, which is the shape of every dead-Tailwind bug on file here. */}
      <div className="px-6 py-4 pb-32 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {phase === 'idle' && (
          <UploadPanel
            busy={uploadMutation.isPending}
            inputRef={fileInputRef}
            onFile={(f) => { setError(null); uploadMutation.mutate(f); }}
          />
        )}

        {phase === 'mapping' && upload && sheet && (
          <>
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-foreground font-semibold">{upload.fileName}</p>
                  <p className="text-sm text-fieldiq-text">
                    {sheet.rows.length} row{sheet.rows.length === 1 ? '' : 's'} on this sheet
                    {upload.sheets.length > 1 && ` · ${upload.sheets.length} sheets in the file`}
                  </p>
                </div>
                {upload.sheets.length > 1 && (
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-fieldiq-text">Sheet</span>
                    <select
                      value={sheetIndex}
                      onChange={(e) => {
                        // A different sheet is a different file as far as the
                        // mapping is concerned — column 2 on one is not column 2
                        // on the next, and carrying it over would silently map
                        // the wrong data.
                        setSheetIndex(Number(e.target.value));
                        setMapping(EMPTY_MAPPING);
                        setResolutions({});
                        setPreviewData(null);
                      }}
                      className="bg-background border border-fieldiq-border rounded-lg px-3 py-2 text-foreground"
                    >
                      {upload.sheets.map((s, i) => (
                        <option key={s.name} value={i}>{s.name} ({s.rows.length})</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>

            <MappingPanel columns={sheet.columns} mapping={mapping} onChange={setColumn} />

            {!mappingComplete && (
              <p className="text-sm text-fieldiq-text">
                Choose the serial number, days and work date columns to see the preview.
              </p>
            )}

            {previewMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-fieldiq-text">
                <Loader2 className="w-4 h-4 animate-spin" /> Building preview…
              </div>
            )}

            {previewData && (
              <PreviewPanel
                preview={previewData}
                resolutions={resolutions}
                onResolve={resolveRow}
                committing={commitMutation.isPending}
                onCommit={() => { setError(null); commitMutation.mutate(); }}
              />
            )}
          </>
        )}

        {phase === 'done' && result && <DonePanel result={result} onAnother={reset} />}
      </div>
    </div>
  );
}

/* ── Gold-standard page header (CLAUDE.md § Page Layout) ──────────────────── */

function Header({ title, onBack, actions }: {
  title: string;
  onBack: () => void;
  actions?: React.ReactNode;
}) {
  return (
    <div className="border-b border-border px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label="Back"
            className="flex items-center gap-2 text-foreground hover:text-amber-400 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        </div>
        <div className="flex items-center gap-3">{actions}</div>
      </div>
    </div>
  );
}

/* ── Step 1 ───────────────────────────────────────────────────────────────── */

function UploadPanel({ busy, inputRef, onFile }: {
  busy: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
}) {
  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-8 text-center">
      <Upload className="w-10 h-10 mx-auto text-fieldiq-accent mb-3" />
      <p className="text-foreground font-semibold">Upload the paper-log spreadsheet</p>
      <p className="text-sm text-fieldiq-text mt-1 mb-5">
        .xlsx, .xls or .csv. Nothing is written until you review every row and commit.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        aria-label="Spreadsheet file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? 'Reading…' : 'Choose file'}
      </button>
    </div>
  );
}

/* ── Step 2 ───────────────────────────────────────────────────────────────── */

function MappingPanel({ columns, mapping, onChange }: {
  columns: string[];
  mapping: CertDayImportMapping;
  onChange: (key: keyof CertDayImportMapping, value: string) => void;
}) {
  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
      <p className="text-foreground font-semibold mb-1">Which column holds what?</p>
      <p className="text-sm text-fieldiq-text mb-4">
        Nothing is guessed. These sheets differ from office to office, and a guess that is
        usually right is a wrong certification day the time it is not.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CERT_IMPORT_COLUMNS.map((col) => (
          <label key={col.key} className="block">
            <span className="text-sm text-foreground font-medium">
              {col.label}
              {col.required && <span className="text-fieldiq-accent"> *</span>}
            </span>
            <select
              value={mapping[col.key] ?? ''}
              onChange={(e) => onChange(col.key, e.target.value)}
              className="mt-1 w-full bg-background border border-fieldiq-border rounded-lg px-3 py-2 text-foreground"
            >
              <option value="">{col.required ? 'Choose a column…' : 'Not in this file'}</option>
              {columns.map((label, i) => (
                <option key={`${label}-${i}`} value={i}>
                  {label || `Column ${i + 1}`}
                </option>
              ))}
            </select>
            <span className="text-xs text-fieldiq-muted">{col.hint}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ── Step 3 ───────────────────────────────────────────────────────────────── */

function PreviewPanel({ preview, resolutions, onResolve, committing, onCommit }: {
  preview: CertDayImportPreview;
  resolutions: Record<string, CertDayImportResolution>;
  onResolve: (rowNumber: number, patch: CertDayImportResolution) => void;
  committing: boolean;
  onCommit: () => void;
}) {
  const c = preview.counts;
  return (
    <>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <Stat label="Rows" value={c.total} />
          <Stat label="Matched on serial" value={c.matched} />
          <Stat label="Chosen by you" value={c.resolved} />
          <Stat label="Still to resolve" value={c.unresolved} tone={c.unresolved > 0 ? 'warn' : undefined} />
          <Stat label="Fix the sheet" value={c.invalid} tone={c.invalid > 0 ? 'bad' : undefined} />
        </div>
        <p className="text-xs text-fieldiq-muted mt-3">
          Nothing has been written. This is what the import would do.
        </p>
      </div>

      {c.duplicateRows > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-start gap-3">
          <Copy className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-amber-300 font-semibold">
              {c.duplicateRows} rows name the same unit on the same day.
            </p>
            <p className="text-fieldiq-text mt-0.5">
              Both will be recorded, and the counter below already includes both. If the log was
              transcribed twice, fix the sheet and upload it again — this cannot tell a
              double-entry from a genuine second run.
            </p>
          </div>
        </div>
      )}

      {preview.counters.length > 0 && (
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl overflow-hidden">
          <p className="text-foreground font-semibold px-4 pt-4 pb-2">Resulting counter, per unit</p>
          <div className="overflow-x-auto">
            {/* Named, so a reader — and a test — can reach this table without
                traversing to it from the heading above. */}
            <table className="w-full text-sm" aria-label="Resulting counter, per unit">
              <thead className="text-left text-fieldiq-muted border-b border-fieldiq-border">
                <tr>
                  <th className="px-4 py-2 font-medium">Unit</th>
                  <th className="px-4 py-2 font-medium">Serial</th>
                  <th className="px-4 py-2 font-medium text-right">Now</th>
                  <th className="px-4 py-2 font-medium text-right">Adding</th>
                  <th className="px-4 py-2 font-medium text-right">After</th>
                </tr>
              </thead>
              <tbody>
                {preview.counters.map((p) => (
                  <tr key={p.equipmentId} className="border-b border-fieldiq-border/50 last:border-0">
                    <td className="px-4 py-2 text-foreground">{p.name}</td>
                    <td className="px-4 py-2 text-fieldiq-text">{dash(p.serialNumber)}</td>
                    {/* NULL is never counted, not zero. */}
                    <td className="px-4 py-2 text-right text-fieldiq-text">{dash(p.counterBefore)}</td>
                    <td className="px-4 py-2 text-right text-fieldiq-accent">+{p.daysAdded}</td>
                    <td className="px-4 py-2 text-right text-foreground font-semibold">{p.counterAfter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Import rows">
            <thead className="text-left text-fieldiq-muted border-b border-fieldiq-border">
              <tr>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Serial in file</th>
                <th className="px-3 py-2 font-medium">Days</th>
                <th className="px-3 py-2 font-medium">Work date</th>
                <th className="px-3 py-2 font-medium">Equipment</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row) => (
                <PreviewRow
                  key={row.rowNumber}
                  row={row}
                  roster={preview.roster}
                  resolution={resolutions[String(row.rowNumber)]}
                  onResolve={onResolve}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* `pr-24` reserves the floating agent button's lane. It is fixed to the
          bottom-right of the VIEWPORT, so bottom padding cannot move a
          right-aligned action out from under it — only horizontal room can.
          Both are here: the padding makes the page scrollable, this keeps the
          primary action legible without scrolling. */}
      <div className="flex flex-wrap items-center justify-end gap-3 pr-24">
        {!preview.committable && (
          <p className="text-sm text-fieldiq-text mr-auto">
            {c.unresolved} row{c.unresolved === 1 ? '' : 's'} still to resolve. Every row is
            imported, or none is.
          </p>
        )}
        <button
          onClick={onCommit}
          // The SERVER's answer, not a count derived here.
          disabled={!preview.committable || committing}
          className="inline-flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {committing ? 'Recording…' : `Import ${c.total} row${c.total === 1 ? '' : 's'}`}
        </button>
      </div>
    </>
  );
}

function PreviewRow({ row, roster, resolution, onResolve }: {
  row: CertDayImportRow;
  roster: CertImportRosterUnit[];
  resolution?: CertDayImportResolution;
  onResolve: (rowNumber: number, patch: CertDayImportResolution) => void;
}) {
  const needsEquipment = row.status === 'ambiguous' || row.status === 'unmatched';
  const needsDate = row.problems.some((p) => p.startsWith('work_date_'));
  // An ambiguous serial narrows the picker to the units that actually carry it;
  // anything else offers the whole tenant.
  const options = row.candidates.length > 0 ? row.candidates : roster;

  return (
    <tr className="border-b border-fieldiq-border/50 last:border-0 align-top">
      <td className="px-3 py-2 text-fieldiq-muted">{row.rowNumber}</td>
      <td className="px-3 py-2">
        <div className="text-foreground">{dash(row.serialRaw)}</div>
        {row.serialKey && row.serialKey !== row.serialRaw && (
          // What actually joined. Shown because "matched" with no sight of the
          // key is a claim the operator has to take on trust.
          <div className="text-xs text-fieldiq-muted">matched as {row.serialKey}</div>
        )}
      </td>
      <td className="px-3 py-2 text-foreground">{dash(row.days)}</td>
      <td className="px-3 py-2">
        {row.workDate ? (
          <span className="text-foreground">{row.workDate}</span>
        ) : needsDate ? (
          <input
            type="date"
            aria-label={`Work date for row ${row.rowNumber}`}
            value={resolution?.workDate ?? ''}
            onChange={(e) => onResolve(row.rowNumber, { workDate: e.target.value })}
            className="bg-background border border-fieldiq-border rounded-lg px-2 py-1 text-foreground text-sm"
          />
        ) : dash(null)}
        {row.workDateRaw && !row.workDate && (
          <div className="text-xs text-fieldiq-muted mt-1">in file: {row.workDateRaw}</div>
        )}
      </td>
      <td className="px-3 py-2 min-w-[16rem]">
        {row.equipment && !needsEquipment ? (
          <div>
            <div className="text-foreground">{row.equipment.name}</div>
            <div className="text-xs text-fieldiq-muted">
              {[row.equipment.type, row.equipment.serialNumber].filter(Boolean).join(' · ')}
            </div>
          </div>
        ) : row.status === 'invalid' ? (
          dash(null)
        ) : (
          <select
            aria-label={`Equipment for row ${row.rowNumber}`}
            value={resolution?.equipmentId ?? ''}
            onChange={(e) => onResolve(row.rowNumber, { equipmentId: e.target.value || null })}
            className="w-full bg-background border border-fieldiq-border rounded-lg px-2 py-1 text-foreground text-sm"
          >
            <option value="">Choose the unit…</option>
            {options.map((u) => (
              <option key={u.id} value={u.id}>{unitLabel(u)}</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[row.status]}`}>
          {CERT_IMPORT_STATUS_LABELS[row.status]}
        </span>
        {row.problems.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {row.problems.map((p) => (
              <li key={p} className="text-xs text-fieldiq-muted">{CERT_IMPORT_PROBLEM_LABELS[p]}</li>
            ))}
          </ul>
        )}
        {row.duplicateOfRows.length > 0 && (
          <div className="text-xs text-amber-400 mt-1">
            same unit and day as row {row.duplicateOfRows.join(', ')}
          </div>
        )}
      </td>
    </tr>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warn' | 'bad' }) {
  const colour = tone === 'bad' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : 'text-foreground';
  return (
    <div>
      <span className={`text-lg font-bold ${colour}`}>{value}</span>
      <span className="text-fieldiq-text ml-2">{label}</span>
    </div>
  );
}

/* ── Step 4 ───────────────────────────────────────────────────────────────── */

function DonePanel({ result, onAnother }: {
  result: CertDayImportCommitResult;
  onAnother: () => void;
}) {
  return (
    <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6">
      <div className="flex items-start gap-3">
        <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-foreground font-semibold text-lg">
            {result.counts.inserted} certification day entr{result.counts.inserted === 1 ? 'y' : 'ies'} recorded
          </p>
          <p className="text-sm text-fieldiq-text mt-1">
            Across {result.counts.unitsTouched} unit{result.counts.unitsTouched === 1 ? '' : 's'} ·{' '}
            {result.counts.matchedBySerial} matched on serial ·{' '}
            {result.counts.resolvedByHand} chosen by hand
          </p>
          <p className="text-xs text-fieldiq-muted mt-3">
            Batch <span className="font-mono">{result.importBatchId}</span>
          </p>
          <p className="text-sm text-fieldiq-text mt-3">
            Each entry is on its unit's certification card, with who imported it and how it was
            matched. Nothing here can be edited — a mistake is corrected with a new entry that
            says what changed.
          </p>
          <button
            onClick={onAnother}
            className="mt-4 bg-fieldiq-card border border-fieldiq-border text-foreground font-semibold px-4 py-2 rounded-lg text-sm hover:bg-secondary/50 transition-colors"
          >
            Import another file
          </button>
        </div>
      </div>
    </div>
  );
}
